import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

import type { CommercialCalibration } from "@/api/commercialCalibration";
import { formatBrl } from "@/components/projects/diagnostic/diagnosticData";
import { formatCount } from "@/lib/format";

type CurrentBase = CommercialCalibration["current_base"];
type Missing = CurrentBase["missing"][number];

const OVERLINE = "font-mono text-[10px] uppercase tracking-[0.14em] text-[#7b8799]";

/**
 * A base do cálculo (EF-FUNC-012 v0.1 RF-03): o TCV da BOM do Pricing App, as
 * horas e o custo médio da Calibragem técnica, e o custo do projeto.
 */
export function BaseSummary({ base }: { base: CurrentBase }) {
  const { t } = useTranslation();
  const cards = [
    {
      key: "tcv",
      label: t("SAP software · TCV"),
      value: base.software_tcv == null ? "—" : formatBrl(base.software_tcv),
      note:
        base.contract_period_months == null
          ? t("Investment (BOM)")
          : t("Investment (BOM, {{months}} months)", { months: base.contract_period_months }),
    },
    {
      key: "hours",
      label: t("Project hours"),
      value: base.project_hours == null ? "—" : formatCount(base.project_hours, { decimals: 2 }),
      note: t("Technical calibration"),
    },
    {
      key: "avg-cost",
      label: t("Average cost / HH"),
      value: base.avg_cost_per_hour == null ? "—" : formatBrl(base.avg_cost_per_hour),
      note: t("Technical calibration"),
    },
    {
      key: "cost",
      label: t("Project cost"),
      value: base.project_cost == null ? "—" : formatBrl(base.project_cost),
      note: t("hours × average cost"),
    },
  ];

  return (
    <div data-testid="commercial-base" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((card) => (
        <div key={card.key} className="rounded-lg border border-[#e8ebf0] bg-white p-4">
          <div className={OVERLINE}>{card.label}</div>
          <div className="mt-1 text-lg font-semibold tabular-nums text-[#08254c]">
            {card.value}
          </div>
          <div className="mt-1 text-[11px] text-[#596b85]">{card.note}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * O que falta para haver base, e onde resolver. Sem ela não há conta nem
 * gravação: o servidor recusa com o mesmo código.
 */
export function BaseMissingNotice({ missing }: { missing: Missing[] }) {
  const { t } = useTranslation();
  const messages: Record<Missing, string> = {
    pricing_app_bom: t(
      "Load the Pricing App BOM in node 06 of the Functional: the contract TCV comes from it.",
    ),
    published_calibration: t(
      "Publish the technical calibration: the project hours come from it.",
    ),
    avg_cost_per_hour: t(
      "Publish the technical calibration again with the average team cost per hour: it was published before the rate became a cost.",
    ),
  };

  return (
    <div
      data-testid="commercial-base-missing"
      className="flex items-start gap-2 rounded-md border border-[#e7d3a3] bg-[#fdf8ec] px-4 py-3 text-sm text-[#7a5600]"
    >
      <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-[#b57a00]" />
      <div>
        <p className="font-semibold">{t("The commercial calibration has no base yet")}</p>
        <ul className="mt-1 list-disc pl-4">
          {missing.map((code) => (
            <li key={code}>{messages[code]}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
